import { Directive, ElementRef, Input, OnDestroy, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { AncTooltipBubbleComponent } from './anc-tooltip-bubble.component';

/** Browsers replay mouseenter right after a tap; ignore it for this long (ms). */
const TOUCH_MOUSE_GUARD_MS = 800;
/** Keeps the arrow off the bubble's rounded corners (px). */
const ARROW_EDGE_INSET = 12;

/**
 * Body-anchored (CDK Overlay) replacement for the native `title` tooltip — escapes
 * any ancestor `overflow: hidden` (e.g. text-truncated grid cells) and renders the
 * Figma Design System tooltip bubble instead of the browser's plain UA tooltip.
 *
 * Opens on hover, on keyboard focus, and on tap for touch input (OEM-7023, FR-070),
 * so it never depends on hover alone.
 */
@Directive({
    selector: '[libAncTooltip]',
    standalone: true,
    host: {
        '(mouseenter)': 'onMouseEnter()',
        '(mouseleave)': 'onMouseLeave()',
        '(focusin)': 'onFocusIn()',
        '(focusout)': 'onFocusOut()',
        '(pointerup)': 'onPointerUp($event)'
    }
})
export class AncTooltipDirective implements OnDestroy {
    @Input('libAncTooltip') tooltipText = '';
    /** When true, only shows the tooltip if the host's own text is actually truncated (ellipsis). */
    @Input() ancTooltipOnlyIfTruncated = false;
    /**
     * Which side of the host the bubble renders on. Defaults to 'bottom' (arrow
     * pointing up into the host) — the only variant the bubble's arrow CSS
     * originally supported. 'top' flips both the overlay position and the arrow.
     */
    @Input() ancTooltipPlacement: 'top' | 'bottom' = 'bottom';
    /**
     * When true, the tooltip stays open while the pointer travels from the host
     * into the bubble itself (instead of closing on the host's mouseleave), and
     * its text is selectable — so the value can be copied out of the tooltip.
     */
    @Input() ancTooltipSelectable = false;
    /** When true (default), a tap toggles the tooltip on touch input; a tap elsewhere closes it. */
    @Input() ancTooltipTouch = true;

    private overlayRef: OverlayRef | null = null;
    private closeTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private outsideTapSub: Subscription | null = null;
    private positionSub: Subscription | null = null;
    private lastTouchAt = 0;
    private readonly onBubbleMouseEnter = (): void => this.cancelPendingClose();
    private readonly onBubbleMouseLeave = (): void => this.close();

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef
    ) {}

    onMouseEnter(): void {
        if (Date.now() - this.lastTouchAt < TOUCH_MOUSE_GUARD_MS) {
            return;
        }
        this.open();
    }

    onFocusIn(): void {
        this.open();
    }

    onFocusOut(): void {
        this.close();
    }

    onPointerUp(event: PointerEvent): void {
        if (event.pointerType !== 'touch' || !this.ancTooltipTouch) {
            return;
        }
        this.lastTouchAt = Date.now();
        if (this.overlayRef) {
            this.close();
            return;
        }
        this.open();
        this.outsideTapSub = this.overlayRef?.outsidePointerEvents().subscribe((outside) => {
            if (!this.elementRef.nativeElement.contains(outside.target as Node)) {
                this.close();
            }
        }) ?? null;
    }

    private open(): void {
        const el = this.elementRef.nativeElement;
        if (!this.tooltipText) {
            return;
        }
        if (this.ancTooltipOnlyIfTruncated && el.offsetWidth >= el.scrollWidth) {
            return;
        }

        this.cancelPendingClose();
        if (this.overlayRef) {
            return;
        }

        const isTop = this.ancTooltipPlacement === 'top';
        const origin = this.getTextOrigin(el);
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(origin)
            .withPositions([
                isTop
                    ? { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -6 }
                    : { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 6 }
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });

        const componentRef = this.overlayRef.attach(new ComponentPortal(AncTooltipBubbleComponent, this.viewContainerRef));
        componentRef.setInput('text', this.tooltipText);
        componentRef.setInput('selectable', this.ancTooltipSelectable);
        componentRef.setInput('placement', this.ancTooltipPlacement);

        // Near a viewport edge CDK pushes the bubble sideways to keep it on screen; move the
        // arrow with it so it still points at the text (OEM-7023: last grid columns on tablets).
        componentRef.changeDetectorRef.detectChanges();
        this.overlayRef.updatePosition();
        this.alignArrow(origin);
        this.positionSub = positionStrategy.positionChanges.subscribe(() => this.alignArrow(origin));

        if (this.ancTooltipSelectable) {
            const bubbleEl = this.overlayRef.overlayElement;
            bubbleEl.addEventListener('mouseenter', this.onBubbleMouseEnter);
            bubbleEl.addEventListener('mouseleave', this.onBubbleMouseLeave);
        }
    }

    /**
     * Centers on the actual (natural, un-clipped) text width rather than the host
     * element's own box — the host can be wider (flex-stretched) or narrower
     * (ellipsis-truncated) than its text, which would otherwise off-center the bubble.
     * Walks text nodes only (not element boxes) so a leading icon or a block-level
     * wrapper (e.g. an icon + text flex row) can't pull the measurement wide.
     */
    private getTextOrigin(el: HTMLElement): { x: number; y: number; width: number; height: number } {
        const elRect = el.getBoundingClientRect();
        try {
            const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
                acceptNode: (node) =>
                    node.textContent && node.textContent.trim().length > 0
                        ? NodeFilter.FILTER_ACCEPT
                        : NodeFilter.FILTER_REJECT
            });
            let left = Infinity;
            let right = -Infinity;
            let node: Node | null;
            while ((node = walker.nextNode())) {
                const range = document.createRange();
                range.selectNodeContents(node);
                for (const r of Array.from(range.getClientRects())) {
                    if (r.width === 0 && r.height === 0) continue;
                    left = Math.min(left, r.left);
                    right = Math.max(right, r.right);
                }
            }
            if (isFinite(left) && isFinite(right) && right > left) {
                // Clamp to the host's visible box so overflowing/ellipsis text
                // doesn't pull the tooltip center into neighboring cells, and to any
                // clipping ancestor (e.g. a table scrolled sideways inside its card).
                const visible = this.getVisibleBounds(el, elRect);
                left = Math.max(left, visible.left);
                right = Math.min(right, visible.right);
                if (right > left) {
                    return { x: left, y: elRect.top, width: right - left, height: elRect.height };
                }
            }
        } catch {
            // Fall through to the element's own box.
        }
        return { x: elRect.x, y: elRect.y, width: elRect.width, height: elRect.height };
    }

    /** The host's horizontal extent after clipping by ancestors that hide overflow. */
    private getVisibleBounds(el: HTMLElement, elRect: DOMRect): { left: number; right: number } {
        let left = elRect.left;
        let right = elRect.right;
        for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
            if (getComputedStyle(parent).overflowX !== 'visible') {
                const rect = parent.getBoundingClientRect();
                left = Math.max(left, rect.left);
                right = Math.min(right, rect.right);
            }
        }
        return { left, right };
    }

    /** Points the arrow at the origin's centre, wherever the bubble ended up. */
    private alignArrow(origin: { x: number; width: number }): void {
        const bubble = this.overlayRef?.overlayElement.querySelector<HTMLElement>('.anc-tooltip-bubble');
        if (!bubble) {
            return;
        }
        const rect = bubble.getBoundingClientRect();
        if (!rect.width) {
            return;
        }
        const target = origin.x + origin.width / 2 - rect.left;
        const arrowX = Math.min(Math.max(target, ARROW_EDGE_INSET), rect.width - ARROW_EDGE_INSET);
        bubble.style.setProperty('--anc-tooltip-arrow-x', `${arrowX}px`);
    }

    onMouseLeave(): void {
        if (!this.ancTooltipSelectable) {
            this.close();
            return;
        }
        // Give the pointer a moment to reach the bubble before closing —
        // it renders a few px below/above the host, not flush against it.
        this.cancelPendingClose();
        this.closeTimeoutId = setTimeout(() => this.close(), 150);
    }

    private cancelPendingClose(): void {
        if (this.closeTimeoutId != null) {
            clearTimeout(this.closeTimeoutId);
            this.closeTimeoutId = null;
        }
    }

    private close(): void {
        this.cancelPendingClose();
        this.outsideTapSub?.unsubscribe();
        this.outsideTapSub = null;
        this.positionSub?.unsubscribe();
        this.positionSub = null;
        if (this.ancTooltipSelectable && this.overlayRef) {
            const bubbleEl = this.overlayRef.overlayElement;
            bubbleEl.removeEventListener('mouseenter', this.onBubbleMouseEnter);
            bubbleEl.removeEventListener('mouseleave', this.onBubbleMouseLeave);
        }
        this.overlayRef?.dispose();
        this.overlayRef = null;
    }

    ngOnDestroy(): void {
        this.close();
    }
}
